import {
  CallCreationRequest,
  CallDto,
  CallWithFeedItemDto,
  CommunicationGuidanceDto,
  CreateVoiceCallAuditRequestDto,
  FilterGroupDto,
  PaginatedCallsDto,
  PaginatedUsersDto,
  PhoneNumberTwilioDetailsDto,
  ReturnCallCreationRequestDto,
  SuggestedActionDto,
  SuggestedNeedDto,
  SuggestedScheduledEventDto,
  SuggestionAccuracyFeedbackRequest,
  SuggestionsDto,
  UpdateCallResponseDto,
  ValidateActiveCallsResponseDto,
  VoiceCallAuditDto,
  VoiceCallRecordingDto,
  VoiceCallRecordingUpdateDto,
} from '@act/shared/data-transfer-objects';
import type { AddResourceToCallRequest } from '@act/shared/types';
import { CallUpdateRequest } from '@act/shared/types';
import { EndpointDefinition } from '../utils/endpointDefinition';
import type { CommunicationTiming } from '@act/shared/types';

export const callsRequests = {
  getCalls: EndpointDefinition.get<
    PaginatedCallsDto,
    {
      orderBy?: string;
      orderDirection?: string;
      'page-start'?: number;
      'page-size'?: number;
      guideIds?: string[];
      statuses?: string[];
      directionType?: 'incoming' | 'outgoing';
      callType?: 'unknown' | 'resource' | 'feedback' | 'unassigned';
    }
  >({
    name: 'getCalls',
    route: '/calls',
  }),

  /**
   * Full call detail including recording transcription / LLM summary text.
   * Use after selecting a call; list GET /calls omits those fields.
   */
  getCallById: EndpointDefinition.get<CallDto, { id: number }>({
    name: 'getCallById',
    route: '/calls/:id',
  }),

  createCall: EndpointDefinition.post<
    CallWithFeedItemDto,
    CallCreationRequest,
    null
  >({
    name: 'createCall',
    route: '/calls',
  }),

  returnCall: EndpointDefinition.post<
    CallWithFeedItemDto,
    ReturnCallCreationRequestDto,
    null
  >({
    name: 'returnCall',
    route: '/calls/return',
  }),

  updateCall: EndpointDefinition.patch<
    UpdateCallResponseDto,
    CallUpdateRequest,
    { id: number }
  >({
    name: 'updateCall',
    route: '/calls/:id',
  }),

  updateSubjectId: EndpointDefinition.patch<
    CallDto,
    { subjectId: string },
    { id: number }
  >({
    name: 'updateSubjectId',
    route: '/calls/:id/subjectId',
  }),

  unassignCall: EndpointDefinition.delete<
    CallDto,
    {
      id: number;
    }
  >({
    name: 'unassignCall',
    route: '/calls/:id/subjectId',
  }),

  getMembersForCallAssignment: EndpointDefinition.get<
    PaginatedUsersDto,
    {
      id: number;
    }
  >({
    name: 'getMembersForCallAssignment',
    route: '/calls/:id/members',
  }),

  updateCallNoFollowUpNeeded: EndpointDefinition.post<
    CallDto,
    { noFollowUpNeeded: boolean },
    { callId: number }
  >({
    name: 'updateCallNoFollowUpNeeded',
    route: '/calls/:callId/no_follow_up_needed',
  }),

  getPhoneDetailsFromTwilio: EndpointDefinition.get<
    PhoneNumberTwilioDetailsDto,
    { phoneNumber: string }
  >({
    name: 'getPhoneDetailsFromTwilio',
    route: '/interaction/phone-numbers/twilio-details',
  }),

  getCallsFilters: EndpointDefinition.get<FilterGroupDto, { named?: string }>({
    name: 'getCallsFilters',
    route: '/calls/filters',
  }),

  updateVoiceCallRecording: EndpointDefinition.patch<
    VoiceCallRecordingDto,
    VoiceCallRecordingUpdateDto,
    { id: number }
  >({
    name: 'updateVoiceCallRecording',
    route: '/calls/voice-call-recording/:id',
  }),

  createCallReviewTask: EndpointDefinition.post<
    VoiceCallAuditDto[],
    CreateVoiceCallAuditRequestDto,
    { callId: number }
  >({
    name: 'createCallReviewTasks',
    route: '/calls/:callId/audit',
  }),

  getCallReviewTask: EndpointDefinition.get<
    VoiceCallAuditDto[],
    { callId: number; assigneeId?: string }
  >({
    name: 'getCallReviewTask',
    route: '/calls/:callId/audit/tasks',
  }),

  updateCallReview: EndpointDefinition.patch<
    VoiceCallAuditDto,
    { actionTaken?: boolean },
    { auditId: number }
  >({
    name: 'updateCallReview',
    route: '/calls/audit/tasks/:auditId',
  }),

  getCommunicationGuidances: EndpointDefinition.get<
    CommunicationGuidanceDto[],
    {
      memberId?: string;
      clientId?: number;
      communicationTiming?: CommunicationTiming;
    }
  >({
    name: 'getCommunicationGuidances',
    route: '/interaction/guidance',
  }),

  getActiveCalls: EndpointDefinition.get<CallDto[], null>({
    name: 'getActiveCalls',
    route: '/calls/active',
  }),

  validateActiveCalls: EndpointDefinition.post<
    ValidateActiveCallsResponseDto,
    { callIds: number[] },
    null
  >({
    name: 'validateActiveCalls',
    route: '/calls/validate-active',
  }),

  getSuggestions: EndpointDefinition.get<SuggestionsDto, { id: number }>({
    name: 'getSuggestions',
    route: '/calls/:id/llm_suggestions',
  }),

  addGuideToCall: EndpointDefinition.post<
    CallDto,
    { guideId: string; isColdTransfer: boolean; memberId?: string },
    { callId: number }
  >({
    name: 'addGuideToCall',
    route: '/calls/:callId/add-guide',
  }),

  addResourceToCall: EndpointDefinition.post<
    CallDto,
    AddResourceToCallRequest,
    { callId: number }
  >({
    name: 'addResourceToCall',
    route: '/calls/:callId/add-resource',
  }),

  mergeCallConnections: EndpointDefinition.post<
    CallWithFeedItemDto,
    { callId: number },
    null
  >({
    name: 'mergeCallConnections',
    route: '/calls/merge-call-connections',
  }),

  toggleConnectionHold: EndpointDefinition.post<
    CallDto,
    { connectionId: number; onHold: boolean },
    null
  >({
    name: 'toggleConnectionHold',
    route: '/calls/toggle-connection-hold',
  }),

  hangupConnection: EndpointDefinition.post<
    CallDto,
    { connectionId: number },
    null
  >({
    name: 'hangupConnection',
    route: '/calls/hangup-connection',
  }),

  createSuggestionAccuracyFeedback: EndpointDefinition.post<
    SuggestedNeedDto | SuggestedScheduledEventDto | SuggestedActionDto,
    SuggestionAccuracyFeedbackRequest,
    { suggestionId: string }
  >({
    name: 'suggestionAccuracyFeedback',
    route: '/calls/:suggestionId/accuracyFeedback',
  }),

  undoCreatedSuggestion: EndpointDefinition.delete<
    SuggestedNeedDto | SuggestedActionDto | SuggestedScheduledEventDto,
    {
      id: number;
      suggestionType: 'need' | 'action' | 'scheduled_event';
      suggestionId: string;
    }
  >({
    name: 'undoCreatedSuggestion',
    route: '/calls/:id/llm_suggestions/type/:suggestionType/:suggestionId',
  }),

  rerunTranscription: EndpointDefinition.post<
    VoiceCallRecordingDto,
    null,
    { id: number }
  >({
    name: 'rerunTranscription',
    route: '/calls/:id/rerun-transcription',
  }),
};
